import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Car, 
  ArrowRight,
  Signal,
  CheckCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';

const StatCard = ({ title, value, icon: Icon, color, loading }) => {
  const colorClasses = {
    primary: 'text-blue-600',
    info: 'text-cyan-600',
    success: 'text-green-600'
  };
  
  return (
    <Card className="h-full border border-gray-200 rounded-lg">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-4">
          <div className="text-sm text-gray-600">
            {loading ? <Skeleton className="w-20 h-4" /> : title}
          </div>
          {loading ? (
            <Skeleton className="w-6 h-6 rounded-full" />
          ) : (
            <Icon className={`w-5 h-5 ${colorClasses[color]}`} />
          )}
        </div>
        {loading ? (
          <Skeleton className="w-16 h-8" />
        ) : (
          <div className="text-2xl font-medium text-gray-900">
            {value}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function FleetWidget({ loading = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Mock data - in a real app, this would come from an API
  const fleetData = {
    totalDevices: 24,
    online: 18,
    activeDevices: 14
  };
  
  const handleNavigateToFleet = () => {
    navigate('/dashboard/fleet/devices');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.5 }}
      className="h-full"
    >
      <Card className="h-full rounded-xl border border-gray-200 overflow-visible relative">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {loading ? <Skeleton className="w-32 h-6" /> : t('dashboard.fleet.title')}
            </h3>
            <Button 
              variant="ghost"
              size="sm"
              onClick={handleNavigateToFleet}
              disabled={loading}
              className="text-blue-600 hover:text-blue-700"
            >
              {loading ? <Skeleton className="w-20 h-4" /> : (
                <>
                  {t('dashboard.general.viewAll', 'View All')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard 
              title={t('dashboard.fleet.totalDevices')} 
              value={fleetData.totalDevices} 
              icon={Car}
              color="primary"
              loading={loading}
            />
            <StatCard 
              title={t('dashboard.fleet.connectivity')} 
              value={`${fleetData.online}/${fleetData.totalDevices}`} 
              icon={Signal}
              color="info"
              loading={loading}
            />
            <StatCard 
              title={t('dashboard.fleet.activeDevices')} 
              value={fleetData.activeDevices} 
              icon={CheckCircle}
              color="success"
              loading={loading}
            />
          </div>
          
          {!loading && (
            <div className="flex items-center justify-center">
              <div className="relative inline-flex items-center justify-center">
                <div className="w-24 h-24 relative">
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      className="text-gray-200"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      strokeDasharray={`${75 * 2.51} ${100 * 2.51}`}
                      className="text-blue-600"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-semibold text-gray-900">75%</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
} 